import { useState } from 'react';
import { teamAPI, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, RefreshCw, Pencil } from 'lucide-react';
import SelectorDePersona from '@/components/SelectorDePersona';
import { toast } from 'sonner';

/**
 * El organigrama de una persona: lo que dijo M365 y lo que alguien corrigió a mano.
 *
 * **Las dos verdades se muestran juntas cuando difieren**, y eso es deliberado. Que el área
 * corregida tape a la del directorio sin decir cuál era esconde el dato más útil: si M365 dice
 * una cosa y una persona escribió otra, alguien de los dos lo tiene mal y conviene saber quién.
 *
 * **Y la corrección se puede cargar para cualquiera**, tenga o no perfil de M365 (decisión de
 * Diego, 2026-08-31). El caso no es sólo el cliente sin directorio: es que M365 diga mal el área
 * de alguien y tarden meses en arreglarlo. Hasta entonces el agente atiende con el dato malo.
 */
const CAMPOS = [
  { key: 'job_title', label: 'Cargo', placeholder: 'Analista funcional' },
  { key: 'department', label: 'Área / Gerencia', placeholder: 'SISTEMAS' },
  { key: 'office_location', label: 'Ubicación', placeholder: 'Córdoba' },
];

export default function OrganigramaDeLaPersona({
  persona, puedeEditar = false, onCambio, refreshing = false, onRefresh,
}) {
  const m365 = persona?.org_profile || {};
  const manual = persona?.perfil_manual || {};
  const jefe = persona?.jefe || null;

  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [borrador, setBorrador] = useState({});

  const correo = (persona?.identities || []).find(
    (i) => i.channel_type === 'email' && i.is_primary,
  )?.external_id_normalized
    || (persona?.identities || []).find((i) => i.channel_type === 'email')?.external_id_normalized;

  const abrir = () => {
    setBorrador(Object.fromEntries(CAMPOS.map((c) => [c.key, manual[c.key] || ''])));
    setEditando(true);
  };

  const guardar = async () => {
    if (!correo) { toast.error('Esta persona no tiene correo: el organigrama se edita por correo'); return; }
    setGuardando(true);
    try {
      await teamAPI.setPerfilManual(correo, borrador);
      toast.success('Organigrama corregido');
      setEditando(false);
      onCambio?.();
    } catch (e) {
      toast.error(formatApiError(e, 'No se pudo guardar'));
    } finally { setGuardando(false); }
  };

  const cambiarJefe = async (contactId) => {
    // **Decirlo en vez de no hacer nada.** Era un `return` mudo: se elegía un jefe, no pasaba
    // nada, no aparecía ningún mensaje, y la única lectura posible desde afuera es «esto está
    // roto». El jefe se define por correo porque es la identidad estable de una persona entre
    // el organigrama y la consola.
    if (!correo) {
      toast.error('Esta persona no tiene correo cargado, y el jefe se define por correo. '
                  + 'Agregale una identidad de email primero.');
      return;
    }
    try {
      await teamAPI.setJefe(correo, contactId || null);
      toast.success(contactId ? 'Jefe cargado a mano' : 'Vuelve a mandar el organigrama de M365');
      onCambio?.();
    } catch (e) {
      toast.error(formatApiError(e, 'No se pudo cambiar el jefe'));
    }
  };

  const fila = (c) => {
    const efectivo = manual[c.key] || m365[c.key] || '';
    const corregido = Boolean(manual[c.key]);
    const difiere = corregido && m365[c.key] && m365[c.key] !== manual[c.key];
    return (
      <div key={c.key} className="flex items-baseline gap-2 py-1 border-b border-brand-border last:border-0">
        <span className="text-[10px] uppercase tracking-wide text-zinc-500 w-32 shrink-0">{c.label}</span>
        <span className="text-xs text-zinc-800">{efectivo || <span className="text-zinc-400">—</span>}</span>
        {corregido && <Badge variant="outline" className="text-[9px] rounded-sm">a mano</Badge>}
        {difiere && (
          <span className="text-[10px] text-amber-700" title="M365 dice otra cosa. Alguno de los dos está mal.">
            M365 dice «{m365[c.key]}»
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="border border-brand-border rounded-sm p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-[10px] uppercase tracking-wide text-zinc-500">Organigrama</p>
        <div className="flex items-center gap-1">
          {onRefresh && (
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={onRefresh} disabled={refreshing}>
              {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              <span className="ml-1">M365</span>
            </Button>
          )}
          {puedeEditar && !editando && (
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={abrir}>
              <Pencil className="w-3 h-3 mr-1" />Corregir
            </Button>
          )}
        </div>
      </div>

      {editando ? (
        <div className="space-y-2">
          <p className="text-[10px] text-zinc-500">
            Lo que escribas acá <b>le gana a M365</b> y el sync no lo pisa. Vaciar un campo
            devuelve el valor del directorio — no se pierde, sigue guardado abajo.
          </p>
          {CAMPOS.map((c) => (
            <div key={c.key}>
              <label className="text-[10px] uppercase tracking-wide text-zinc-500">{c.label}</label>
              <Input
                className="h-8 text-xs rounded-sm mt-0.5"
                placeholder={m365[c.key] || c.placeholder}
                value={borrador[c.key] ?? ''}
                data-testid={`organigrama-${c.key}`}
                onChange={(e) => setBorrador((b) => ({ ...b, [c.key]: e.target.value }))}
              />
            </div>
          ))}
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs" onClick={() => setEditando(false)}>Cancelar</Button>
            <Button size="sm" className="h-7 rounded-sm text-xs" disabled={guardando} onClick={guardar}>
              {guardando && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}Guardar
            </Button>
          </div>
        </div>
      ) : (
        <div>
          {CAMPOS.map(fila)}

          <div className="flex items-baseline gap-2 py-1 mt-1">
            <span className="text-[10px] uppercase tracking-wide text-zinc-500 w-32 shrink-0">Jefe</span>
            <span className="text-xs text-zinc-800">
              {jefe?.nombre || jefe?.email || <span className="text-zinc-400">sin jefe</span>}
            </span>
            {jefe?.origen === 'manual' && <Badge variant="outline" className="text-[9px] rounded-sm">a mano</Badge>}
            {jefe?.email && !jefe?.contact_id && (
              <span className="text-[10px] text-amber-700" title="El correo existe en el directorio pero no hay una persona cargada con él.">
                no existe como persona
              </span>
            )}
          </div>

          {puedeEditar && (
            <div className="mt-1">
              {/* Se **elige** de la lista, no se tipea: un correo escrito a mano que no
                  corresponde a nadie se ve igual que uno válido, y el jefe abre su bandeja,
                  la ve vacía y no tiene forma de saber si es que no tiene gente o que el
                  vínculo está roto.

                  Era un `<select>` plano sobre 233 personas, que es elegir de una lista sólo en
                  teoría: para encontrar a alguien había que saber cómo quedó escrito su nombre
                  en el organigrama. Ahora se escribe y filtra. */}
              {/* **Busca en el directorio entero y no en lo que hay en pantalla.** Recibía
                  `personas`, que es el resultado de la búsqueda de arriba: si alguien filtraba
                  por «Barbagallo» para encontrar a la persona, los únicos jefes elegibles eran
                  los Barbagallo. El jefe de alguien no tiene por qué parecerse a su nombre. */}
              <SelectorDePersona
                testId="selector-de-jefe"
                value={jefe?.origen === 'manual' ? (jefe?.contact_id || '') : ''}
                onChange={(contactId) => cambiarJefe(contactId)}
                excluir={persona?.contact_id}
                sinElegir={`— según M365${jefe?.origen !== 'manual' && jefe?.nombre ? ` (${jefe.nombre})` : ''} —`}
                placeholder="Escribí el nombre del jefe…"
              />
              <p className="text-[10px] text-zinc-500 mt-1">
                El jefe cargado acá <b>le gana al organigrama</b>. Y decide qué ve en
                «Mis solicitudes»: los casos de su gente, para poder ordenarlos.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
